import { LINK_INVITE_EXPIRY_DAYS } from "@nfp-tracker/utils";
import { supabase } from "./client";
import { invokeEdgeFunction } from "./functions";

export type InstructorClient = {
  linkId: string;
  clientUserId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  displayName: string;
  accessLevel: "read" | "write";
  linkedAt: string;
  goal: string | null;
  methods: string[];
  lastObservationDate: string | null;
  observationCount: number;
};

export type InstructorClientPage = {
  clients: InstructorClient[];
  totalCount: number;
};

export type LinkedInstructor = {
  linkId: string;
  instructorUserId: string;
  accessLevel: "read" | "write";
  linkedAt: string;
  displayName: string | null;
  publicEmail: string | null;
  companyName: string | null;
  profilePhotoUrl: string | null;
};

export type PendingInstructorInvite = {
  id: string;
  inviteeEmail: string;
  token: string;
  accessLevel: "read" | "write";
  expiresAt: string;
  createdAt: string;
  // Deliberately no invitee identity/account fields: list_my_pending_instructor_invites returns
  // only the invite row, so a client cannot use invites to probe whether an email has an account.
};

export type InstructorInviteAcceptResult = {
  ok: boolean;
  reason?:
    | "not_authenticated"
    | "invite_not_found"
    | "invite_expired"
    | "email_mismatch"
    | "not_an_instructor"
    | "instructor_profile_missing";
  clientUserId?: string;
};

export const INSTRUCTOR_CLIENT_PAGE_SIZE = 20;

/** Instructor links are read-only by design; see accept_instructor_invite() in the migration. */
export const INSTRUCTOR_LINK_ACCESS_LEVEL = "read" as const;

const displayNameFor = (
  firstName: string | null,
  lastName: string | null,
  email: string | null
) => [firstName, lastName].filter(Boolean).join(" ").trim() || email || "Unnamed client";

export const listInstructorClients = async ({
  search,
  limit = INSTRUCTOR_CLIENT_PAGE_SIZE,
  offset = 0,
}: {
  search?: string | null;
  limit?: number;
  offset?: number;
} = {}): Promise<InstructorClientPage> => {
  const { data, error } = await supabase.rpc("list_my_instructor_clients", {
    p_search: search?.trim() || null,
    p_limit: limit,
    p_offset: offset,
  });

  if (error) {
    throw error;
  }

  const rows = data || [];

  return {
    // `total_count` is duplicated onto every row by the RPC so paging needs no second query;
    // an empty page legitimately means zero matches.
    totalCount: rows.length ? Number(rows[0].total_count) : 0,
    clients: rows.map((row) => ({
      linkId: row.link_id,
      clientUserId: row.client_user_id,
      firstName: row.first_name,
      lastName: row.last_name,
      email: row.email,
      displayName: displayNameFor(row.first_name, row.last_name, row.email),
      accessLevel: (row.access_level === "write" ? "write" : "read") as "read" | "write",
      linkedAt: row.linked_at,
      goal: row.goal,
      methods: row.methods || [],
      lastObservationDate: row.last_observation_date,
      observationCount: Number(row.observation_count || 0),
    })),
  };
};

export type InstructorClientSummary = {
  clientUserId: string;
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  goal: string | null;
  methods: string[];
  lastObservationDate: string | null;
  observationCount: number;
};

/**
 * Loads one client by id for the client-chart header, for the case where the instructor deep
 * links or reloads on `/instructor/clients/:id/...` and never went through the roster.
 *
 * Returns null when the link is gone (revoked, or an id that was never theirs) so callers can
 * bounce back to the roster instead of rendering a header for data they cannot read. The RLS
 * policies would refuse the reads anyway; the explicit link check is what turns that into a
 * clean "not your client" answer rather than an empty row.
 */
export const loadInstructorClientSummary = async (
  clientUserId: string
): Promise<InstructorClientSummary | null> => {
  const { data: canView, error: canViewError } = await supabase.rpc("instructor_can_view_client", {
    p_client_id: clientUserId,
  });

  if (canViewError) {
    throw canViewError;
  }

  if (!canView) {
    return null;
  }

  const [accountResult, profileResult, latestResult, countResult] = await Promise.all([
    supabase
      .from("user_accounts")
      .select("first_name, last_name, email")
      .eq("user_id", clientUserId)
      .maybeSingle(),
    supabase
      .from("user_tracking_profiles")
      .select("goal, methods")
      .eq("user_id", clientUserId)
      .maybeSingle(),
    supabase
      .from("observations")
      .select("observation_date")
      .eq("owner_user_id", clientUserId)
      .order("observation_date", { ascending: false })
      .limit(1)
      .maybeSingle(),
    supabase
      .from("observations")
      .select("id", { count: "exact", head: true })
      .eq("owner_user_id", clientUserId),
  ]);

  if (accountResult.error) {
    throw accountResult.error;
  }

  const account = accountResult.data;
  if (!account) {
    return null;
  }

  return {
    clientUserId,
    displayName: displayNameFor(account.first_name, account.last_name, account.email),
    firstName: account.first_name,
    lastName: account.last_name,
    email: account.email,
    goal: profileResult.data?.goal ?? null,
    methods: profileResult.data?.methods ?? [],
    lastObservationDate: latestResult.data?.observation_date ?? null,
    observationCount: countResult.count ?? 0,
  };
};

/**
 * Records that an instructor deliberately opened a client's chart. Best-effort: a failed log
 * write must never block the instructor from doing their job, so this swallows errors.
 */
export const logInstructorClientView = async (clientUserId: string): Promise<void> => {
  const { error } = await supabase.rpc("log_instructor_client_view", {
    p_client_id: clientUserId,
  });

  if (error) {
    console.error("[instructor] Failed to log client view", error);
  }
};

export const listMyInstructors = async (): Promise<LinkedInstructor[]> => {
  const { data, error } = await supabase.rpc("list_my_instructors");

  if (error) {
    throw error;
  }

  return (data || []).map((row) => ({
    linkId: row.link_id,
    instructorUserId: row.instructor_user_id,
    accessLevel: (row.access_level === "write" ? "write" : "read") as "read" | "write",
    linkedAt: row.linked_at,
    displayName: row.display_name,
    publicEmail: row.public_email,
    companyName: row.company_name,
    profilePhotoUrl: row.profile_photo_url,
  }));
};

export const listMyPendingInstructorInvites = async (): Promise<PendingInstructorInvite[]> => {
  const { data, error } = await supabase.rpc("list_my_pending_instructor_invites");

  if (error) {
    throw error;
  }

  return (data || []).map((row) => ({
    id: row.id,
    inviteeEmail: row.invitee_email,
    token: row.token,
    accessLevel: (row.access_level === "write" ? "write" : "read") as "read" | "write",
    expiresAt: row.expires_at,
    createdAt: row.created_at,
  }));
};

/**
 * Client-initiated: the tracking user invites an instructor to view their chart. Instructor
 * links are read-only — an instructor logging observations on a client's behalf is a separate
 * feature, not something an invite should silently grant.
 */
export const inviteInstructor = async ({
  clientUserId,
  instructorEmail,
  senderName,
}: {
  clientUserId: string;
  instructorEmail: string;
  senderName: string;
}): Promise<PendingInstructorInvite> => {
  const normalizedEmail = instructorEmail.trim().toLowerCase();
  const token = crypto.randomUUID().replace(/-/g, "");
  const expiresAt = new Date();
  expiresAt.setDate(expiresAt.getDate() + LINK_INVITE_EXPIRY_DAYS);

  const { data: invite, error } = await supabase
    .from("link_invites")
    .insert({
      inviter_user_id: clientUserId,
      invitee_email: normalizedEmail,
      link_type: "instructor",
      // Always read-only; accept_instructor_invite() also ignores this value and forces 'read'.
      access_level: INSTRUCTOR_LINK_ACCESS_LEVEL,
      token,
      expires_at: expiresAt.toISOString(),
    })
    .select("id, invitee_email, token, access_level, expires_at, created_at")
    .single();

  if (error) {
    throw error;
  }

  const { error: emailError } = await invokeEdgeFunction("send-invite-email", {
    body: {
      inviteId: invite.id,
      recipientEmail: normalizedEmail,
      recipientName: normalizedEmail.split("@")[0],
      senderName,
      inviteToken: token,
      linkType: "instructor",
    },
  });

  if (emailError) {
    throw emailError;
  }

  return {
    id: invite.id,
    inviteeEmail: invite.invitee_email,
    token: invite.token,
    accessLevel: INSTRUCTOR_LINK_ACCESS_LEVEL,
    expiresAt: invite.expires_at,
    createdAt: invite.created_at,
  };
};

export const cancelInstructorInvite = async (inviteId: string): Promise<void> => {
  const { error } = await supabase
    .from("link_invites")
    .update({ canceled_at: new Date().toISOString() })
    .eq("id", inviteId)
    .eq("link_type", "instructor");

  if (error) {
    throw error;
  }
};

export const revokeInstructorLink = async (linkId: string): Promise<void> => {
  const { error } = await supabase
    .from("user_data_links")
    .update({ is_active: false, revoked_at: new Date().toISOString() })
    .eq("id", linkId)
    .eq("link_type", "instructor");

  if (error) {
    throw error;
  }
};

/**
 * PostgREST reports a missing RPC as PGRST202 ("Could not find the function ... in the schema
 * cache"), which the JS client surfaces as an error object rather than a thrown 404. It happens
 * when an app build ships ahead of the migration that creates the function.
 */
const isMissingRpcError = (error: { code?: string; message?: string } | null): boolean => {
  if (!error) return false;
  if (error.code === "PGRST202" || error.code === "404") return true;
  return /could not find the function/i.test(error.message || "");
};

/**
 * Returns null both when no instructor invite matches the token and when the
 * `get_instructor_invite_by_token` RPC does not exist yet. Callers try the spouse RPC first, so a
 * database that predates the instructor migration must never make a spouse invite look broken.
 */
export const getInstructorInviteByToken = async (token: string) => {
  const { data, error } = await supabase.rpc("get_instructor_invite_by_token", {
    invite_token: token,
  });

  if (error) {
    if (isMissingRpcError(error)) {
      console.warn("[instructor] get_instructor_invite_by_token is not deployed yet", {
        code: error.code,
      });
      return null;
    }
    throw error;
  }

  const row = (data || [])[0];
  if (!row) {
    return null;
  }

  return {
    id: row.id,
    inviteeEmail: row.invitee_email,
    inviterUserId: row.inviter_user_id,
    inviterName: row.inviter_name,
    expiresAt: row.expires_at,
    accepted: row.accepted,
    createdAt: row.created_at,
  };
};

export const acceptInstructorInvite = async (
  inviteId: string
): Promise<InstructorInviteAcceptResult> => {
  const { data, error } = await supabase.rpc("accept_instructor_invite", {
    invite_id: inviteId,
  });

  if (error) {
    throw error;
  }

  return (data || { ok: false, reason: "invite_not_found" }) as InstructorInviteAcceptResult;
};

export type InstructorProfile = {
  instructorId: string;
  userId: string;
  displayName: string;
  publicEmail: string | null;
  companyName: string | null;
  profilePhotoUrl: string | null;
  bio: string | null;
  isListedPublicly: boolean;
};

export const loadInstructorProfile = async (
  userId: string
): Promise<InstructorProfile | null> => {
  const { data, error } = await supabase
    .from("instructor_profiles")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  if (!data) {
    return null;
  }

  return {
    instructorId: data.instructor_id,
    userId: data.user_id,
    displayName: data.display_name,
    publicEmail: data.public_email,
    companyName: data.company_name,
    profilePhotoUrl: data.profile_photo_url,
    bio: data.bio,
    isListedPublicly: data.is_listed_publicly,
  };
};

export const upsertInstructorProfile = async (input: {
  userId: string;
  displayName: string;
  publicEmail?: string | null;
  companyName?: string | null;
  bio?: string | null;
  isListedPublicly?: boolean;
}): Promise<void> => {
  const { error } = await supabase.from("instructor_profiles").upsert(
    {
      user_id: input.userId,
      display_name: input.displayName.trim(),
      public_email: input.publicEmail?.trim() || null,
      company_name: input.companyName?.trim() || null,
      bio: input.bio?.trim() || null,
      is_listed_publicly: input.isListedPublicly ?? false,
    },
    { onConflict: "user_id" }
  );

  if (error) {
    throw error;
  }
};
